import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Microservico {
  id: number;
  item: string;
  nome: string;
  status: string;
  descricao: string;
  ativo: boolean;
}

@Component({
  selector: 'app-consultar-microservicos',
  templateUrl: './consultar-microservicos.component.html',
  styleUrls: ['./consultar-microservicos.component.scss']
})
export class ConsultarMicroservicosComponent implements OnInit {

  microservicos: Microservico[] = [];
  microservicosFiltrados: Microservico[] = [];
  microservicosPaginados: Microservico[] = [];

  // Filtros
  filtroItem: string = '';
  filtroNome: string = '';
  filtroStatus: string = '';

  paginaAtual: number = 1;
  itensPorPagina: number = 10;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.carregarMicroservicos();
  }

  carregarMicroservicos(): void {
    const url = API + 'empresa/Microservicos/Consultar';
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.util.closeLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];

        this.microservicos = lista
          .filter((item: any) => item && typeof item === 'object')
          .map((item: any) => ({
            id: Number(item.id || 0),
            item: this.toText(item.item),
            nome: this.toText(item.descricao),
            descricao: this.toText(item.descricao),
            ativo: this.parseBoolean(item.ativo),
            status: this.parseBoolean(item.ativo) ? 'Ativo' : 'Inativo'
          }))
          .filter((item: Microservico) => item.id > 0);

        this.aplicarFiltros();
      },
      error: () => {
        this.util.closeLoading();
        this.microservicos = [];
        this.microservicosFiltrados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar os microserviços.');
      }
    });
  }

  aplicarFiltros(): void {
    this.microservicosFiltrados = this.microservicos.filter(microservico => {
      const matchItem = !this.filtroItem ||
        microservico.item.toLowerCase().includes(this.filtroItem.toLowerCase());
      const matchNome = !this.filtroNome ||
        microservico.nome.toLowerCase().includes(this.filtroNome.toLowerCase());
      const matchStatus = !this.filtroStatus ||
        microservico.status.toLowerCase() === this.filtroStatus.toLowerCase();

      return matchItem && matchNome && matchStatus;
    });

    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  limparFiltros(): void {
    this.filtroItem = '';
    this.filtroNome = '';
    this.filtroStatus = '';
    this.aplicarFiltros();
  }

  editarMicroservico(microservico: Microservico): void {
    this.router.navigate(['/cadastro-microservicos'], {
      queryParams: {
        modo: 'edicao',
        idMicroservico: microservico.id
      }
    });
  }

  excluirMicroservico(microservico: Microservico): void {
    const acao = microservico.ativo ? 'desativar' : 'ativar';
    if (!confirm(`Deseja ${acao} o microserviço "${microservico.nome}"?`)) return;
    this.alterarStatus(microservico);
  }

  visualizarMicroservico(microservico: Microservico): void {
    this.util.toasterShowSuccess('Microserviço', `${microservico.item} - ${microservico.nome}`);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'ativo': return 'badge bg-success';
      case 'inativo': return 'badge bg-secondary';
      default: return 'badge bg-light text-dark';
    }
  }

  alterarStatus(microservico: Microservico): void {
    const novoAtivo = !microservico.ativo;
    const payload = {
      ID_MICROSERVICOS: microservico.id,
      ITEM: microservico.item,
      DESCRICAO: microservico.descricao,
      ATIVO: novoAtivo
    };

    const url = API + 'empresa/Microservicos/SaveAsync';
    this.util.showLoading();
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível atualizar o status.');
          return;
        }

        microservico.ativo = novoAtivo;
        microservico.status = novoAtivo ? 'Ativo' : 'Inativo';
        this.aplicarFiltros();
        this.util.toasterShowSuccess('Sucesso', novoAtivo ? 'Microserviço ativado com sucesso.' : 'Microserviço desativado com sucesso.');
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao atualizar status do microserviço.');
      }
    });
  }

  get totalPaginas(): number {
    if (this.itensPorPagina <= 0) return 1;
    return Math.max(1, Math.ceil(this.microservicosFiltrados.length / this.itensPorPagina));
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  paginaAnterior(): void {
    if (this.paginaAtual <= 1) return;
    this.paginaAtual--;
    this.atualizarPaginacao();
  }

  proximaPagina(): void {
    if (this.paginaAtual >= this.totalPaginas) return;
    this.paginaAtual++;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    if (!this.microservicosFiltrados.length) {
      this.microservicosPaginados = [];
      return;
    }

    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.microservicosPaginados = this.microservicosFiltrados.slice(inicio, fim);
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const text = this.toText(value).toLowerCase();
    return text === '1' || text === 'true' || text === 'sim' || text === 'yes';
  }
} 